import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PaperPlaneTilt, Clock, Paperclip, UserCircle, Eye, ChatCircleDots, ArrowClockwise } from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const TYPES = {
  email_sent: { icon: PaperPlaneTilt, color: "#00E5FF", verb: (m) => <>Email envoyé{m?.to_email ? <> à <span className="font-medium">{m.to_email}</span></> : ""}</> },
  followup_sent: { icon: ArrowClockwise, color: "#00E5FF", verb: (m) => <>Relance envoyée{m?.to_email ? <> à <span className="font-medium">{m.to_email}</span></> : ""}</> },
  email_opened: { icon: Eye, color: "#F5A623", verb: (m) => <><span className="text-[#F5A623] font-medium">Email ouvert</span>{m?.to_email ? <> par {m.to_email}</> : ""}</> },
  email_received: { icon: ChatCircleDots, color: "#22C55E", verb: (m) => <><span className="text-[#22C55E] font-medium">Réponse reçue</span>{m?.from_email ? <> de {m.from_email}</> : ""}</> },
};

// Reusable CRM activity timeline. Pass exactly one scope prop.
export function ActivityTimeline({ opportunityId, companyId, contactId, consultantId, refreshKey, title = "Activité" }) {
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (opportunityId) params.opportunity_id = opportunityId;
    if (companyId) params.company_id = companyId;
    if (contactId) params.contact_id = contactId;
    if (consultantId) params.consultant_id = consultantId;
    try {
      const { data } = await api.get("/crm/timeline", { params });
      setItems(data);
    } catch {
      setItems([]);
    }
  }, [opportunityId, companyId, contactId, consultantId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="surface rounded-lg p-6" data-testid="activity-timeline">
      <div className="overline mb-4 flex items-center gap-2"><Clock size={14} className="text-accent" /> {title}</div>
      {items === null ? (
        <div className="text-xs text-muted animate-pulse">Chargement…</div>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted" data-testid="activity-empty">Aucune activité pour l'instant. Contactez un décideur pour démarrer le suivi.</p>
      ) : (
        <ul className="space-y-4" data-testid="activity-list">
          {items.map((a) => {
            const t = TYPES[a.type] || TYPES.email_sent;
            const Icon = t.icon;
            return (
              <li key={a.id} className="flex gap-3" data-testid={`activity-${a.id}`}>
                <div className="mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: `${t.color}1f`, border: `1px solid ${t.color}44` }}>
                  <Icon size={13} style={{ color: t.color }} weight="fill" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-fg">{t.verb(a.metadata)}</div>
                  {a.metadata?.subject && <div className="text-xs text-fg/70 mt-0.5 truncate">Objet : {a.metadata.subject}</div>}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted mt-1">
                    {a.metadata?.consultant_name && <span className="flex items-center gap-1"><UserCircle size={12} /> {a.metadata.consultant_name}</span>}
                    {(a.metadata?.attachments || []).length > 0 && <span className="flex items-center gap-1"><Paperclip size={12} /> {a.metadata.attachments.join(", ")}</span>}
                    {a.user_name && <span>par {a.user_name}</span>}
                    <span className="font-mono">{timeAgo(a.created_at)}</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
